import * as ThreadLayout from "~/page-layouts/thread-layout";
import { Tooltip } from "~/components/Tooltip";
import { createContext, useMemo, useState } from "react";
import { createUseContextHook } from "~/utils/createUseContextHook";
import { RecordPointer, RecordValue } from "libs/schema";
import { Avatar } from "~/components/Avatar";
import { useThread } from "~/hooks/useThread";
import { useUsersPermittedToViewThread } from "~/hooks/useUsersPermittedToViewThread";
import { useOrganizationMembersOfThread } from "~/hooks/useOrganizationMembersOfThread";
import { useUsersSubscribedToThread } from "~/hooks/useUsersSubscribedToThread";
import { useNewUsersWhoWillReceiveNotificationForDraftReply } from "~/hooks/useNewUsersWhoWillReceiveNotificationForDraftReply";
import { IoMdClose, IoMdCloseCircle } from "react-icons/io";
import {
  completeRequestCommand,
  hideThreadInfoPanelCommand,
  openApplyLabelDialogCommand,
  removeLabelCommand,
} from "~/utils/common-commands";
import { ParentComponent } from "~/utils/type-helpers";
import { useThreadLabelIds } from "~/hooks/useThreadLabelIds";
import { ThreadLabelChip } from "~/components/LabelChip";
import { FaRegPlusSquare } from "react-icons/fa";
import { useThreadRequests } from "~/hooks/useThreadRequests";
import { useUserProfile } from "~/hooks/useUserProfile";
import { groupBy } from "lodash-comms";
import { stringComparer } from "libs/comparers";
import { useAuthGuardContext } from "~/route-guards/withAuthGuard";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { formatRequestDueDate } from "~/utils/time-formatting";
import { DateTime } from "luxon";

/* -------------------------------------------------------------------------------------------------
 *  ThreadInfoPanel
 * -------------------------------------------------------------------------------------------------
 */

export const ThreadInfoPanel: ParentComponent<{ threadId: string }> = (props) => {
  const { focusedEntry } = useThreadInfoPanelContext();

  const isDraftFocused = focusedEntry?.table === "draft";

  return (
    <ThreadLayout.InfoPanel onBackdropClick={() => hideThreadInfoPanelCommand.trigger()}>
      <div className="p-4">
        <div className="h-4" />
        <h2 className="text-lg font-medium text-slate-9">Thread details</h2>

        {isDraftFocused && <DraftAudience draftId={focusedEntry.id} />}

        <ThreadLabels threadId={props.threadId} />

        <ThreadAudience threadId={props.threadId} />

        <ThreadRequests threadId={props.threadId} />

        <ThreadPermissions threadId={props.threadId} />

        <ClosePanelButton />
      </div>
    </ThreadLayout.InfoPanel>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const ClosePanelButton: ParentComponent = () => {
  return (
    <Tooltip side="top" content="Close details panel">
      <button
        type="button"
        className="absolute bottom-4 right-4 text-slate-9 hover:text-black"
        onClick={(e) => {
          e.preventDefault();
          hideThreadInfoPanelCommand.trigger();
        }}
      >
        <IoMdClose className="hidden text-4xl md-w:block" />
        <IoMdCloseCircle className="text-5xl md-w:hidden" />
      </button>
    </Tooltip>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const ThreadAudience: ParentComponent<{ threadId: string }> = (props) => {
  const [audienceProfiles] = useUsersSubscribedToThread({ threadId: props.threadId });

  return (
    <>
      <div className="flex items-center">
        <Tooltip side="bottom" content="Replies will be delivered to the inbox of these users.">
          <p className="mt-4 text-slate-9 cursor-help">Recipients (?)</p>
        </Tooltip>
      </div>

      <div className="mt-2 flex flex-wrap">
        {audienceProfiles.map((profile) => (
          <Tooltip key={profile.id} side="bottom" content={profile.name}>
            <div className="p-1">
              <Avatar label={profile.name} photoURL={profile.photo_url} />
            </div>
          </Tooltip>
        ))}
      </div>
    </>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const ThreadLabels: ParentComponent<{ threadId: string }> = (props) => {
  const [labelIds] = useThreadLabelIds({ threadId: props.threadId });

  return (
    <>
      <div className="mt-4 flex items-center">
        <Tooltip side="bottom" content="Only you can see these labels">
          <p className="text-slate-9 cursor-help">Labels (?)</p>
        </Tooltip>

        <div className="flex-1" />

        <Tooltip side="bottom" content="Apply label...">
          <button
            type="button"
            className="text-slate-9 hover:text-black"
            onClick={() => {
              openApplyLabelDialogCommand.trigger({ location: "ThreadLabels" });
            }}
          >
            <FaRegPlusSquare />
          </button>
        </Tooltip>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {labelIds.length === 0 && "No labels"}

        {labelIds.map((labelId) => (
          <ThreadLabelChip
            key={labelId}
            labelId={labelId}
            color="primary"
            showArchived
            onRemoveButtonClick={({ label }) => {
              removeLabelCommand.trigger(label.id, {
                location: "ThreadLabels",
              });
            }}
          />
        ))}
      </div>
    </>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const ThreadPermissions: ParentComponent<{ threadId: string }> = (props) => {
  const [thread] = useThread(props.threadId);

  const [permissions] = useUsersPermittedToViewThread({
    threadId: thread?.visibility === "PRIVATE" ? thread.id : null,
  });

  const [organizationMembers] = useOrganizationMembersOfThread({
    threadId: thread?.visibility === "SHARED" ? thread.id : null,
  });

  return (
    <>
      <div className="flex items-center">
        <Tooltip
          side="bottom"
          content={`These users have permission to view this thread.
            They may or may not be subscribed to notifications
            for this thread.`}
        >
          <p className="mt-4 text-slate-9 cursor-help">Can view (?)</p>
        </Tooltip>
      </div>

      <div className="mt-2 flex flex-wrap">
        {thread?.visibility === "SHARED" ?
          organizationMembers.map((m) => `Everyone in the ${m.name}.`).join(" ")
        : permissions.map((profile) => (
            <Tooltip key={profile.id} side="bottom" content={profile.name}>
              <div className="p-1">
                <Avatar label={profile.name} photoURL={profile.photo_url} />
              </div>
            </Tooltip>
          ))
        }
      </div>
    </>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const DraftAudience: ParentComponent<{ draftId: string }> = (props) => {
  const [newAudienceProfiles, { isLoading }] = useNewUsersWhoWillReceiveNotificationForDraftReply({
    draftId: props.draftId,
  });

  return (
    <div className="mt-4 p-4 rounded-md bg-slate-3">
      <p className="text-black text-md font-bold">Draft</p>

      <Tooltip side="bottom" content="These new recipients will be added to the existing thread recipients.">
        <p className="text-black cursor-help">Adds recipients (?)</p>
      </Tooltip>

      <div className="mt-2 flex flex-wrap">
        {newAudienceProfiles.length === 0 && isLoading ?
          "Loading..."
        : newAudienceProfiles.length === 0 ?
          <span className="text-sm font-medium">This reply doesn't contain any new recipients.</span>
        : newAudienceProfiles.map((profile) => (
            <Tooltip key={profile.id} side="bottom" content={profile.name}>
              <div className="p-1">
                <Avatar label={profile.name} photoURL={profile.photo_url} />
              </div>
            </Tooltip>
          ))
        }
      </div>
    </div>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const ThreadRequests: ParentComponent<{ threadId: string }> = (props) => {
  const { currentUserId } = useAuthGuardContext();
  const [requests] = useThreadRequests({ threadId: props.threadId });

  const openRequests = useMemo(() => {
    const groupedRequests = groupBy(
      requests
        .filter((r) => !r.resolved_at)
        .sort((a, b) => stringComparer(a.respond_by, b.respond_by) || stringComparer(a.id, b.id)),
      (r) => r.recipient_user_id,
    );

    const requestsForCurrentUser = [...(groupedRequests[currentUserId] || []), ...(groupedRequests["null"] || [])].sort(
      (a, b) => stringComparer(a.respond_by, b.respond_by) || stringComparer(a.id, b.id),
    );

    delete groupedRequests[currentUserId];
    delete groupedRequests["null"];
    const requestsForOthers = Object.entries(groupedRequests);

    return {
      areAnyRequests: requestsForCurrentUser.length > 0 || requestsForOthers.length > 0,
      requestsForCurrentUser,
      requestsForOthers,
    };
  }, [requests, currentUserId]);

  return (
    <>
      {openRequests.requestsForCurrentUser.length > 0 && (
        <>
          <div className="flex items-center">
            <Tooltip
              side="bottom"
              content={`Pending requests awaiting your response. Note that changes made by 
                messages scheduled to be sent in the future will not be reflected here.`}
            >
              <p className="mt-4 text-slate-9 cursor-help">Awaiting response (?)</p>
            </Tooltip>
          </div>

          <div className="mt-2">
            {openRequests.requestsForCurrentUser.map((request) => (
              <ThreadRequest key={request.id} request={request} />
            ))}
          </div>
        </>
      )}

      {openRequests.requestsForOthers.length > 0 && (
        <>
          <div className="flex items-center">
            <Tooltip side="bottom" content="Pending requests for other people.">
              <p className="mt-4 text-slate-9 cursor-help">Other requests (?)</p>
            </Tooltip>
          </div>

          <div className="mt-2 flex flex-wrap">
            {openRequests.requestsForOthers.map(([userId, requests]) => (
              <OtherThreadRequest key={userId} userId={userId} requests={requests} />
            ))}
          </div>
        </>
      )}
    </>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const ThreadRequest: ParentComponent<{ request: RecordValue<"thread_request"> }> = (props) => {
  const [profile] = useUserProfile(props.request.sender_user_id);

  const respondByMsg = useMemo(() => {
    const dueDate = DateTime.fromISO(props.request.respond_by, { zone: props.request.respond_by_tz || undefined });

    return formatRequestDueDate({
      dueDate,
      priority: String(props.request.priority),
    });
  }, [props.request.respond_by, props.request.respond_by_tz, props.request.priority]);

  if (!profile) return null;

  return (
    <button
      type="button"
      className="flex items-start group hover:cursor-pointer"
      onClick={() => {
        completeRequestCommand.trigger(props.request.id);
      }}
    >
      <div className="mr-2 mt-1 flex items-center justify-center">
        <MdCheckBoxOutlineBlank className="ml-1 group-hover:hidden" />
        <MdCheckBox className="ml-1 hidden group-hover:block" />
      </div>

      <span className="text-left">
        {profile.name} due {respondByMsg.short}
      </span>
    </button>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const OtherThreadRequest: ParentComponent<{ userId: string; requests: RecordValue<"thread_request">[] }> = (props) => {
  const [profile] = useUserProfile(props.userId);

  const soonestRequest = useMemo(() => {
    return props.requests.toSorted((a, b) => {
      if (a.respond_by === b.respond_by) return 0;
      return DateTime.fromISO(a.respond_by) < DateTime.fromISO(b.created_at) ? -1 : 1;
    })[0];
  }, [props.requests]);

  if (!profile || !soonestRequest) return null;

  let tooltipContent: string;
  const requestTimeFromNow = DateTime.fromISO(soonestRequest.respond_by).toRelative();

  if (requestTimeFromNow === null) {
    tooltipContent = `${profile.name} has a request but the due date is invalid`;
  } else if (props.requests.length === 1) {
    tooltipContent = `${profile.name} has a request due ${requestTimeFromNow}`;
  } else {
    tooltipContent = `${profile.name} has requests from ${props.requests.length} people with first due ${requestTimeFromNow}`;
  }

  return (
    <Tooltip side="bottom" content={tooltipContent}>
      <div className="p-1">
        <Avatar label={profile.name} photoURL={profile.photo_url} />
      </div>
    </Tooltip>
  );
};

/* -------------------------------------------------------------------------------------------------
 *  ThreadInfoPanelContext
 * -------------------------------------------------------------------------------------------------
 */

export type ThreadInfoPanelContext = {
  focusedEntry: RecordPointer<"draft"> | null;
  setFocusedEntry: (value: RecordPointer<"draft"> | null) => void;
};

const ThreadInfoPanelContext = createContext<ThreadInfoPanelContext | null>(null);

export const ProvideThreadInfoPanelContext: ParentComponent = (props) => {
  const [focusedEntry, setFocusedEntry] = useState<ThreadInfoPanelContext["focusedEntry"]>(null);

  const context = useMemo((): ThreadInfoPanelContext => {
    return {
      focusedEntry,
      setFocusedEntry,
    };
  }, [focusedEntry, setFocusedEntry]);

  return <ThreadInfoPanelContext.Provider value={context}>{props.children}</ThreadInfoPanelContext.Provider>;
};

export const useThreadInfoPanelContext = createUseContextHook(ThreadInfoPanelContext, "ThreadInfoPanelContext");

/* -----------------------------------------------------------------------------------------------*/
